import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useSubscription, useQuery } from 'hooks/apollo';
import { ACCOUNT_STATEMENT_DETAILS_QUERY, ACCOUNT_TYPES } from '../../queries';
import { RAW_FINANCIAL_INDICATORS_QUERY } from '../../../../financialIndicators/components/list/queries';
import { ACCOUNT_STATEMENT_DETAIL_UPDATED, ACCOUNT_STATEMENT_DETAIL_DELETED } from '../../subscriptions';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import AccountStatementDetailsContainer from './AccountStatementDetailsContainer';
import AccountStatementDetailsModalComponent from './AccountStatementDetailsModalComponent';
import { useToast } from 'hooks/toasts/useToast';
import { Add } from '@mui/icons-material';
import { Grid, Box, Icon, Typography } from '@mui/material';

export const accountStatementDetailsStore = getListBrowserStore({
  withQueryFilters: false,
  filterDefinitions: [
    {
      name: 'accountName',
      caption: 'AccountStatement.Details.AccountName',
      type: 'text'
    },
    {
      name: 'accountSymbol',
      caption: 'AccountStatement.Details.AccountSymbol',
      type: 'text'
    },
    {
      name: 'attributName',
      caption: 'AccountStatement.Details.AttributName',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'AccountStatement.Details.OrderNo',
      value: 'OrderNo'
    },
    {
      caption: 'AccountStatement.Details.AccountName',
      value: 'AccountName'
    },
    {
      caption: 'AccountStatement.Details.AccountSymbol',
      value: 'AccountSymbol'
    },
    {
      caption: 'AccountStatement.Details.AttributName',
      value: 'AttributName'
    }
  ],
  pager: {
    orderBy: 'OrderNo',
    direction: 'asc'
  }
});

const AccountStatementDetailsBrowser = ({ configId, canWrite, usedByPartner }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { addActions } = useListBrowserUtils(accountStatementDetailsStore);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: ACCOUNT_STATEMENT_DETAILS_QUERY,
        name: 'accountStatementDetails',
        variables: { configId: configId },
        skip: !configId
      }
    }),
    [configId]
  );

  const columns = useMemo(
    () => [
      { name: 'orderNo', caption: 'AccountStatement.Details.OrderNo' },
      { name: 'accountName', caption: 'AccountStatement.Details.AccountName' },
      { name: 'accountSymbol', caption: 'AccountStatement.Details.AccountSymbol' },
      { name: 'accountTypeId', caption: 'AccountStatement.Details.AccountType' },
      { name: 'financialIndicatorId', caption: 'AccountStatement.Details.FinancialIndicatorName' },
      { name: 'attributName', caption: 'AccountStatement.Details.AttributName' },
      { name: 'visibleYN', caption: 'AccountStatement.Details.Visible' },
      { name: 'readOnlyYN', caption: 'AccountStatement.Details.ReadOnly' },
      { name: 'actions', caption: 'Actions', width: '125px' }
    ],
    []
  );

  const { data: accountTypesListData } = useQuery(ACCOUNT_TYPES);
  const accountTypes = useMemo(() => accountTypesListData?.dictionaryAccountTypes || [], [accountTypesListData]);

  const { data: financialIndicatorData } = useQuery(RAW_FINANCIAL_INDICATORS_QUERY);
  const financialIndicatorList = useMemo(() => financialIndicatorData?.financialIndicators || [], [financialIndicatorData]);

  useSubscription(ACCOUNT_STATEMENT_DETAIL_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('AccountStatement.DetailDeleted'), 'success');
      client.cache.evict({
        id: `AccountStatementDetailItem:{"configDetailId":${data.accountStatementDetailDeleted.configDetailId}}`
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'accountStatementDetails' });
      client.cache.gc();
    }
  });

  useSubscription(ACCOUNT_STATEMENT_DETAIL_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('AccountStatement.DetailSaved'), 'success');
      if (data.accountStatementDetailUpdated.isNew) client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'accountStatementDetails' });
      client.cache.gc();
    }
  });

  const openAddModal = useCallback(() => setIsAddModalOpen(true), []);
  const closeAddModal = useCallback(() => setIsAddModalOpen(false), []);

  useEffect(() => {
    addActions([
      {
        name: 'Add',
        icon: <Add />,
        handler: openAddModal,
        disabled: !canWrite
      }
    ]);
  }, [addActions, canWrite, openAddModal, t]);

  return !configId ? (
    <Grid container flexDirection='column' gap={1} p={2}>
      <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
        <Icon fontSize='small' color='primary'>
          error
        </Icon>
        <Typography color='textPrimary'>{t('AccountStatement.DetailsWarning')}</Typography>
      </Box>
    </Grid>
  ) : (
    <div>
      <TableListBrowser
        name='account_statement_details_browser'
        store={accountStatementDetailsStore}
        ItemComponent={AccountStatementDetailsContainer}
        itemComponentProps={{ configId, canWrite, usedByPartner }}
        columns={columns}
        layout='auto'
      />
      <ExportModalComponent store={accountStatementDetailsStore} options={fetchingOptions} />
      <AccountStatementDetailsModalComponent
        isOpen={isAddModalOpen}
        handleCloseModal={closeAddModal}
        accountTypes={accountTypes}
        financialIndicatorList={financialIndicatorList}
        configId={configId}
      />
      <ApolloFetchingComponent store={accountStatementDetailsStore} options={fetchingOptions} />
    </div>
  );
};

AccountStatementDetailsBrowser.propTypes = {
  configId: PropTypes.number,
  canWrite: PropTypes.bool,
  usedByPartner: PropTypes.bool
};
export default AccountStatementDetailsBrowser;
